import { Container, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  FacebookIcon,
  WhatsappIcon,
  TwitterIcon,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinIcon,
} from 'react-share';
import { useRouter } from 'next/router';
import NotFound from 'pages/404';

import { Blog } from 'types';
import axios from 'axios';
import MarkdownIt from 'markdown-it';
import Head from 'next/head';

type PodCafeCardProps = {
  podCafeContent: Blog;
};
const PodCafeContent = ({ podCafeContent }: PodCafeCardProps) => {
  const theme = useTheme();
  const useStyles = makeStyles({
    contentMain: {
      padding: '100px 0px 150px',
      [theme.breakpoints.down('sm')]: {
        padding: [[100, 50, 100]],
      },
      [theme.breakpoints.down('xs')]: {
        padding: [[100, 20, 100]],
      },
      '& p': {
        marginTop: '0px',
      },
      '& h3': {
        marginTop: '40px',
      },
      '& h4': {
        marginBottom: '0px',
      },
      '& img': {
        maxWidth: '100%',
        marginBottom: '20px',
      },
      '& h1': {
        lineHeight: '42px',
        fontSize: '34px',
        paddingRight: '50px',
      },
      '& ul': {
        paddingLeft: '20px',
        '& li': {
          listStyle: 'auto',
          marginBottom: '10px',
          '&::marker': {},
        },
      },
      '& ol': {
        paddingLeft: '20px',
        '& li': {
          listStyle: 'disc',
          marginBottom: '10px',
          '&::marker': {},
        },
      },
      '& a': {
        color: '#008B5D',
        fontWeight: 600,
        textDecoration: 'underline',
      },
    },

    title: {
      [theme.breakpoints.down('sm')]: {
        marginBottom: '50px',
      },
      '& h1': {
        textAlign: 'left',
        lineHeight: '46px',
        fontSize: '36px',
        [theme.breakpoints.down('sm')]: {
          fontSize: '20px',
          lineHeight: 1.5,
        },
      },
    },
    listContent: {
      '& li': {
        listStyle: 'initial',
        '&::marker': {
          fontSize: '25px',
        },
      },
    },
    stepContent: {
      '& li': {
        listStyle: 'auto',
        '&::marker': {
          fontWeight: 700,
        },
        marginBottom: '25px',
      },
      '& span': {
        fontWeight: 800,
        display: 'block',
      },
    },

    socialIcon: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '50px',
      flexDirection: 'column',
      position: 'fixed',
      marginLeft: '-120px',
      fontSize: '14px',
      color: '#333333',
      [theme.breakpoints.down('sm')]: {
        marginLeft: '-65px',
      },
      [theme.breakpoints.down('xs')]: {
        position: 'sticky',
        flexDirection: 'row',
        backgroundColor: '#fff',
        marginLeft: '0px',
        marginTop: '-50px',
        top: '0px',
        width: '100%',
        padding: '20px 0 10px',
      },

      '& h4': {
        margin: '0px auto 0px 0px',
        fontSize: '18px',
      },

      '& button': {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '5px',
        marginBottom: '10px',

        '&:last-child': {
          marginRight: '0px',
        },
      },
    },

    contentLayout: {
      display: 'flex',
      flexWrap: 'wrap',
      maxWidth: '700px',
      margin: 'auto',
    },
    signUpLink: {
      marginTop: ' 30px !important',
      fontSize: '18px',
      color: '#008B5D',
      fontWeight: 600,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    cafeContent: {
      maxWidth: '100%',
    },
  });
  const classes = useStyles();
  const md = new MarkdownIt();

  const router = useRouter();

  const { slug } = router.query;

  return (
    <>
      {podCafeContent ? (
        <>
          <Head>
            <title>{podCafeContent.title}</title>
            <meta name="title" content={podCafeContent.title} />
            <meta
              name="description"
              content={podCafeContent.meta_description}
            />

            <meta property="og:type" content="website" />
            <meta
              property="og:url"
              content={`https://podworld.in/pod-cafe/${podCafeContent.slug}`}
            />
            <meta property="og:title" content={podCafeContent.title} />
            <meta
              property="og:description"
              content={podCafeContent.meta_description}
            />
            <meta
              property="og:image"
              content={podCafeContent.title_image.url}
            />

            <meta
              property="twitter:card"
              content={podCafeContent.meta_description}
            />
            <meta
              property="twitter:url"
              content={`https://podworld.in/pod-cafe/${podCafeContent.slug}`}
            />
            <meta property="twitter:title" content={podCafeContent.title} />
            <meta
              property="twitter:description"
              content={podCafeContent.meta_description}
            />
            <meta
              property="twitter:image"
              content={podCafeContent.title_image.url}
            />
          </Head>
          <div className={classes.contentMain}>
            <Container fixed={true}>
              <div className={classes.contentLayout}>
                <div className={classes.socialIcon}>
                  <p>Share on</p>
                  <FacebookShareButton
                    quote={podCafeContent?.title}
                    url={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/pod-cafe/${slug}`}
                  >
                    <FacebookIcon size={35} round={true} />
                  </FacebookShareButton>
                  <WhatsappShareButton
                    title={podCafeContent?.title}
                    url={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/pod-cafe/${slug}`}
                  >
                    <WhatsappIcon size={35} round={true} />
                  </WhatsappShareButton>
                  <TwitterShareButton
                    title={podCafeContent?.title}
                    url={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/pod-cafe/${slug}`}
                  >
                    <TwitterIcon size={35} round={true} />
                  </TwitterShareButton>
                  <LinkedinShareButton
                    title={podCafeContent?.title}
                    url={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/pod-cafe/${slug}`}
                  >
                    <LinkedinIcon size={35} round={true} />
                  </LinkedinShareButton>
                </div>
                <div className={classes.cafeContent}>
                  <div className={classes.title}>
                    <h1
                      dangerouslySetInnerHTML={{
                        __html: md.render(podCafeContent?.title),
                      }}
                    ></h1>
                  </div>
                  {/* <p
                    dangerouslySetInnerHTML={{
                      __html: md.render(podCafeContent?.meta_description),
                    }}
                  ></p> */}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: md.render(podCafeContent?.content),
                    }}
                  ></div>
                  {/* {podCafeContent?.bottomComponent || null}
                                {!userData && podCafeContent?.showBottomLink ? (
                                    <a className={classes.signUpLink} href="/register">
                                        Sign up now to raise funds for your startup
                                    </a>
                                ) : (
                                    ''
                                )} */}
                </div>
              </div>
            </Container>
          </div>
        </>
      ) : (
        <NotFound />
      )}
    </>
  );
};

export const getStaticPaths = async () => {
  let blogs: any[] = [];
  try {
    const strapiUrl = process.env.STRAPI_URL!;
    const { data } = await axios.get<Blog[]>(
      `${strapiUrl}/blogs?_sort=id:desc`,
    );
    blogs = data;
  } catch (error) {
    console.log('Error');
  }
  return {
    fallback: false,
    paths: blogs.map((blog) => ({
      params: {
        slug: blog?.slug,
      },
    })),
  };
};

export const getStaticProps = async ({ params }: any) => {
  const { slug } = params;
  const strapiUrl = process.env.STRAPI_URL!;
  try {
    const blog = await axios
      .get<Blog[]>(`${strapiUrl}/blogs?slug_eq=${slug}`)
      .then((res) => res.data);
    return {
      props: {
        podCafeContent: blog[0],
      },
      // revalidate: 60 * 60,
    };
  } catch (error) {
    return {
      props: {
        podCafeContent: {},
      },
    };
  }
};

export default PodCafeContent;
